import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PilaCompanySettingsCard } from "@/components/payroll/pila/PilaCompanySettingsCard";
import {
  PILA_STATUS_BADGE_VARIANT,
  formatPilaDate,
  intlLocale,
} from "@/components/payroll/pila/pila-format";
import {
  formatPilaPeriod,
  pilaPeriods,
  type PilaBlocker,
  type PilaPreflight,
  type PilaTotals,
} from "@/lib/api/payroll-pila";
import {
  useGeneratePilaPlanilla,
  useRecordPilaFspAdjustment,
  usePilaAvailability,
  usePilaCatalog,
  usePilaPlanillas,
  usePilaPreflight,
  usePilaSettings,
} from "@/lib/api/payroll-pila.queries";
import { useActiveCompany } from "@/lib/company-context";
import { useMoneyFormatter } from "@/lib/money-format";
import { useTeamPermission } from "@/lib/team-context";
import { translateApiError } from "@/lib/translate-api-error";

export const Route = createFileRoute("/companies/$companyId/nomina/pila/")({
  component: PilaIndexPage,
});

function PilaIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PilaIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function PilaIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const availability = usePilaAvailability(companyId);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("pila.title")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {t("pila.subtitle")}
        </p>
      </div>
      {availability.isPending ? (
        <Spinner />
      ) : availability.data?.enabled ? (
        <PilaWorkspace companyId={companyId} />
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <ShieldCheck className="h-7 w-7 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold">
              {t("pila.disabled.title")}
            </h2>
            <p className="max-w-md text-sm text-muted-foreground">
              {t("pila.disabled.body")}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center rounded-lg border bg-card py-16">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

function PilaWorkspace({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const canWrite = useTeamPermission("payroll.write");
  const settings = usePilaSettings(companyId);
  const catalog = usePilaCatalog(companyId);

  if (settings.isPending) return <Spinner />;
  if (settings.isError || !settings.data) {
    return (
      <ErrorState
        title={t("pila.error_title")}
        error={settings.error}
        onRetry={() => void settings.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }
  return (
    <>
      <PilaCompanySettingsCard
        companyId={companyId}
        settings={settings.data}
        catalog={catalog.data}
        canWrite={canWrite}
      />
      <MonthCard companyId={companyId} canWrite={canWrite} />
      <HistoryCard companyId={companyId} />
    </>
  );
}

function MonthCard({
  companyId,
  canWrite,
}: {
  companyId: string;
  canWrite: boolean;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = intlLocale(i18n.language);
  const periods = pilaPeriods();
  const [period, setPeriod] = useState(periods[0]!);
  const preflight = usePilaPreflight(companyId, period);
  const generate = useGeneratePilaPlanilla(companyId);
  const data = preflight.data;
  const blocked = !data || data.blockers.length > 0 || !!data.livePlanilla;

  async function onGenerate() {
    try {
      const planilla = await generate.mutateAsync({ period });
      if (planilla.status === "rejected")
        toast.error(t("pila.month.generated_rejected"));
      else toast.success(t("pila.month.generated"));
      void navigate({
        to: "/companies/$companyId/nomina/pila/$planillaId",
        params: { companyId, planillaId: planilla.id },
      });
    } catch (error) {
      toast.error(translateApiError(error, t, "pila.error"));
      void preflight.refetch();
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileSpreadsheet className="h-4 w-4 text-primary" />
            {t("pila.month.title")}
          </CardTitle>
          <CardDescription className="text-xs">
            {t("pila.month.subtitle")}
          </CardDescription>
        </div>
        <div className="w-56 space-y-1.5">
          <Label htmlFor="pila-period">{t("pila.month.period")}</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger id="pila-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periods.map((p) => (
                <SelectItem key={p} value={p}>
                  <span className="capitalize">
                    {formatPilaPeriod(p, locale)}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {preflight.isPending ? (
          <Spinner />
        ) : preflight.isError || !data ? (
          <ErrorState
            title={t("pila.month.error_title")}
            error={preflight.error}
            onRetry={() => void preflight.refetch()}
            retryLabel={t("common.retry")}
          />
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              {t("pila.month.health_period", {
                period: formatPilaPeriod(data.period, locale),
                health: formatPilaPeriod(data.healthPeriod, locale),
              })}
            </p>
            {data.livePlanilla ? (
              <Alert variant="info">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>{t("pila.month.live_title")}</AlertTitle>
                <AlertDescription className="flex flex-wrap items-center gap-3">
                  <Badge
                    variant={
                      PILA_STATUS_BADGE_VARIANT[data.livePlanilla.status]
                    }
                  >
                    {t(`pila.status.${data.livePlanilla.status}`)}
                  </Badge>
                  <Link
                    to="/companies/$companyId/nomina/pila/$planillaId"
                    params={{ companyId, planillaId: data.livePlanilla.id }}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {t("pila.month.live_open")}
                  </Link>
                </AlertDescription>
              </Alert>
            ) : null}
            <Blockers companyId={companyId} preflight={data} />
            <FspPayables companyId={companyId} preflight={data} />
            <EmployeesTable companyId={companyId} preflight={data} />
            {data.withoutLiquidation.length > 0 ? (
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  {t("pila.month.without_liquidation_title")}
                </AlertTitle>
                <AlertDescription className="text-xs">
                  {t("pila.month.without_liquidation_body")}{" "}
                  {data.withoutLiquidation.map((e, index) => (
                    <span key={e.employeeId}>
                      {index > 0 ? ", " : ""}
                      <Link
                        to="/companies/$companyId/nomina/empleados/$employeeId"
                        params={{ companyId, employeeId: e.employeeId }}
                        className="font-medium underline-offset-2 hover:underline"
                      >
                        {e.name}
                      </Link>
                    </span>
                  ))}
                </AlertDescription>
              </Alert>
            ) : null}
            {data.estimate ? <Estimate totals={data.estimate} /> : null}
            <div className="flex flex-col items-end gap-2">
              {data.livePlanilla ? null : canWrite ? (
                <Button
                  onClick={() => void onGenerate()}
                  disabled={blocked || generate.isPending}
                  data-testid="pila-generate"
                >
                  {generate.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  {generate.isPending
                    ? t("pila.month.generating")
                    : t("pila.month.generate")}
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t("pila.read_only")}
                </p>
              )}
              {generate.isPending ? (
                <p className="text-xs text-muted-foreground">
                  {t("pila.month.generating_hint")}
                </p>
              ) : null}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Blockers({
  companyId,
  preflight,
}: {
  companyId: string;
  preflight: PilaPreflight;
}) {
  const { t } = useTranslation();
  if (preflight.blockers.length === 0) return null;
  return (
    <Alert variant="destructive" data-testid="pila-blockers">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>{t("pila.month.blocked_title")}</AlertTitle>
      <AlertDescription>
        <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-foreground">
          {preflight.blockers.map((blocker, index) => (
            <li key={`${blocker.code}-${index}`}>
              <BlockerText
                companyId={companyId}
                blocker={blocker}
                preflight={preflight}
              />
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

function BlockerText({
  companyId,
  blocker,
  preflight,
}: {
  companyId: string;
  blocker: PilaBlocker;
  preflight: PilaPreflight;
}) {
  const { t } = useTranslation();
  if ("employeeId" in blocker) {
    return (
      <>
        <Link
          to="/companies/$companyId/nomina/empleados/$employeeId"
          params={{ companyId, employeeId: blocker.employeeId }}
          className="font-medium text-primary hover:underline"
        >
          {blocker.employeeName}
        </Link>
        {": "}
        {t(`pila.blocker.${blocker.code}`)}
        {blocker.code === "EMPLOYEE_HIGH_RISK_LEAVE_FUNDING_REQUIRED" ? (
          <>
            {" "}
            <Link
              to="/companies/$companyId/edit"
              params={{ companyId }}
              hash="payroll-policies"
              className="font-medium text-primary hover:underline"
            >
              {t("pila.blocker.choose_policy")}
            </Link>
          </>
        ) : null}
        {blocker.code === "EMPLOYEE_FSP_OVER_DEDUCTED" ? (
          <FspCorrection
            companyId={companyId}
            period={preflight.period}
            employeeId={blocker.employeeId}
            amount={
              preflight.employees.find(
                (e) => e.employeeId === blocker.employeeId,
              )?.fsp?.overWithheld ?? 0
            }
          />
        ) : null}
        {blocker.liquidationId ? (
          <>
            {" "}
            <Link
              to="/companies/$companyId/nomina/liquidaciones/$liquidationId"
              params={{ companyId, liquidationId: blocker.liquidationId }}
              className="font-medium text-primary hover:underline"
            >
              {t("pila.blocker.open_liquidation")}
            </Link>
          </>
        ) : null}
      </>
    );
  }
  if (blocker.code === "DRAFT_LIQUIDATIONS") {
    return (
      <>
        {t("pila.blocker.DRAFT_LIQUIDATIONS")}{" "}
        {preflight.draftLiquidations.map((draft, index) => (
          <span key={draft.id}>
            {index > 0 ? ", " : ""}
            <Link
              to="/companies/$companyId/nomina/liquidaciones/$liquidationId"
              params={{ companyId, liquidationId: draft.id }}
              className="font-medium text-primary hover:underline"
            >
              {draft.employeeName}
            </Link>
          </span>
        ))}
      </>
    );
  }
  if (blocker.code === "NO_CONFIRMED_LIQUIDATIONS") {
    return (
      <>
        {t("pila.blocker.NO_CONFIRMED_LIQUIDATIONS")}{" "}
        <Link
          to="/companies/$companyId/nomina/liquidaciones"
          params={{ companyId }}
          className="font-medium text-primary hover:underline"
        >
          {t("pila.blocker.go_liquidations")}
        </Link>
      </>
    );
  }
  return <>{t(`pila.blocker.${blocker.code}`)}</>;
}

/** Move FSP payroll withheld beyond the planilla to an amount owed to the employee (2505). */
function FspCorrection({
  companyId,
  period,
  employeeId,
  amount,
}: {
  companyId: string;
  period: string;
  employeeId: string;
  amount: number;
}) {
  const { t } = useTranslation();
  const formatCop = useMoneyFormatter();
  const canWrite = useTeamPermission("payroll.write");
  const record = useRecordPilaFspAdjustment(companyId);
  return (
    <span className="ml-1 inline-flex flex-wrap items-center gap-2">
      <span className="font-mono">{formatCop(amount)}</span>
      {canWrite ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-6 px-2 text-xs"
          disabled={record.isPending}
          data-testid="pila-fsp-correction"
          onClick={async () => {
            try {
              await record.mutateAsync({ period, employeeId });
              toast.success(t("pila.fsp.corrected"));
            } catch (error) {
              toast.error(translateApiError(error, t, "pila.error"));
            }
          }}
        >
          {record.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : null}
          {t("pila.fsp.correct")}
        </Button>
      ) : null}
    </span>
  );
}

/** FSP corrections of the month: amounts owed back to employees, refunded through a normal payment. */
function FspPayables({
  companyId,
  preflight,
}: {
  companyId: string;
  preflight: PilaPreflight;
}) {
  const { t } = useTranslation();
  const formatCop = useMoneyFormatter();
  const adjustments = preflight.fspAdjustments ?? [];
  if (adjustments.length === 0) return null;
  const names = new Map(preflight.employees.map((e) => [e.employeeId, e.name]));
  return (
    <Alert data-testid="pila-fsp-payables">
      <AlertTitle>{t("pila.fsp.payables_title")}</AlertTitle>
      <AlertDescription>
        <p className="text-xs text-muted-foreground">
          {t("pila.fsp.payables_hint")}
        </p>
        <ul className="mt-2 space-y-1 text-xs">
          {adjustments.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-2">
              <Link
                to="/companies/$companyId/nomina/empleados/$employeeId"
                params={{ companyId, employeeId: a.employeeId }}
                className="font-medium text-primary hover:underline"
              >
                {names.get(a.employeeId) ?? a.employeeId}
              </Link>
              <span className="font-mono">{formatCop(a.amount)}</span>
              <span className="text-muted-foreground">
                {t("pila.fsp.payable_detail", {
                  deducted: formatCop(a.deducted),
                  filed: formatCop(a.filed),
                })}
              </span>
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

function EmployeesTable({
  companyId,
  preflight,
}: {
  companyId: string;
  preflight: PilaPreflight;
}) {
  const { t } = useTranslation();
  if (preflight.employees.length === 0) return null;
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("pila.month.employee")}</TableHead>
            <TableHead className="text-right">
              {t("pila.month.liquidations")}
            </TableHead>
            <TableHead>{t("pila.month.ready")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {preflight.employees.map((employee) => (
            <TableRow key={employee.employeeId}>
              <TableCell>
                <Link
                  to="/companies/$companyId/nomina/empleados/$employeeId"
                  params={{ companyId, employeeId: employee.employeeId }}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {employee.name}
                </Link>
              </TableCell>
              <TableCell className="text-right font-mono text-sm">
                {employee.liquidationIds.length}
              </TableCell>
              <TableCell>
                {employee.issues.length === 0 ? (
                  <Badge variant="success">{t("pila.month.ready_ok")}</Badge>
                ) : (
                  <Badge variant="destructive">
                    {t("pila.month.ready_issues", {
                      count: employee.issues.length,
                    })}
                  </Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function Estimate({ totals }: { totals: PilaTotals }) {
  const { t } = useTranslation();
  const formatCop = useMoneyFormatter();
  const items: Array<[string, number]> = [
    [t("pila.totals.pension"), totals.pension + totals.fsp],
    [t("pila.totals.health"), totals.health],
    [t("pila.totals.arl"), totals.arl],
    [t("pila.totals.ccf"), totals.ccf],
    [t("pila.totals.sena_icbf"), totals.sena + totals.icbf],
  ];
  return (
    <div
      className="rounded-lg border bg-muted/30 p-4"
      data-testid="pila-estimate"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{t("pila.month.estimate")}</span>
        <span className="font-mono text-lg font-semibold">
          {formatCop(totals.total)}
        </span>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-5">
        {items.map(([label, value]) => (
          <div key={label}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="font-mono">{formatCop(value)}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-xs text-muted-foreground">
        {t("pila.month.estimate_hint", { count: totals.contributors })}
      </p>
    </div>
  );
}

function HistoryCard({ companyId }: { companyId: string }) {
  const { t, i18n } = useTranslation();
  const formatCop = useMoneyFormatter();
  const locale = intlLocale(i18n.language);
  const list = usePilaPlanillas(companyId);
  const planillas = list.data?.planillas ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("pila.history.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {list.isPending ? (
          <Spinner />
        ) : list.isError ? (
          <ErrorState
            title={t("pila.history.error_title")}
            error={list.error}
            onRetry={() => void list.refetch()}
            retryLabel={t("common.retry")}
          />
        ) : planillas.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("pila.history.empty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pila.history.period")}</TableHead>
                  <TableHead>{t("pila.history.number")}</TableHead>
                  <TableHead className="text-right">
                    {t("pila.history.contributors")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("pila.history.total")}
                  </TableHead>
                  <TableHead>{t("pila.history.limit_date")}</TableHead>
                  <TableHead>{t("pila.history.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {planillas.map((planilla) => (
                  <TableRow key={planilla.id}>
                    <TableCell>
                      <Link
                        to="/companies/$companyId/nomina/pila/$planillaId"
                        params={{ companyId, planillaId: planilla.id }}
                        className="text-sm font-medium capitalize text-primary hover:underline"
                      >
                        {formatPilaPeriod(planilla.period, locale)}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {planilla.payrollNumber ?? "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {planilla.contributors}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCop(
                        planilla.totalToPay ?? planilla.estimatedTotal ?? 0,
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatPilaDate(planilla.limitDate, locale)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={PILA_STATUS_BADGE_VARIANT[planilla.status]}
                      >
                        {t(`pila.status.${planilla.status}`)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
